import openai from './openaiClient';

/**
 * Generates social media post content using GPT-5
 * @param {Object} options - Post generation options
 * @param {string} options.topic - Main topic or theme for the post
 * @param {string} options.tone - Tone of the post (professional, casual, humorous, etc.)
 * @param {string} options.platform - Target social media platform
 * @param {number} options.maxLength - Maximum character length
 * @param {string[]} options.keywords - Keywords to include in the post
 * @returns {Promise<Object>} Generated post content with metadata
 */
export async function generatePost(options) {
  const { topic, tone = 'professional', platform = 'general', maxLength = 280, keywords = [] } = options;

  try {
    const keywordText = keywords?.length > 0 ? ` Include these keywords naturally: ${keywords?.join(', ')}.` : '';
    
    const prompt = `Create an engaging ${platform} social media post about "${topic}".
    
Requirements:
- Tone: ${tone}
- Maximum length: ${maxLength} characters
- Make it attention-grabbing and authentic${keywordText}
- Include relevant hashtags if appropriate for ${platform}
- Focus on value and engagement

Generate only the post content, no explanations.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert social media content creator who specializes in creating engaging, authentic posts that drive organic engagement and build communities.' 
        },
        { role: 'user', content: prompt },
      ],
      reasoning_effort: 'low',
      verbosity: 'low',
      max_completion_tokens: 500
    });

    const content = response?.choices?.[0]?.message?.content?.trim();

    return {
      content,
      metadata: {
        topic,
        tone,
        platform,
        generatedAt: new Date()?.toISOString(),
        characterCount: content?.length
      }
    };
  } catch (error) {
    console.error('Error generating post:', error);
    throw new Error('Failed to generate post content. Please try again.');
  }
}

/**
 * Generates multiple post variations for A/B testing
 * @param {Object} options - Post generation options
 * @param {number} variations - Number of variations to generate
 * @returns {Promise<Array>} Array of generated post variations
 */
export async function generatePostVariations(options, variations = 3) {
  try {
    const posts = await Promise.all(
      Array.from({ length: variations }, () => generatePost(options))
    );
    return posts;
  } catch (error) {
    console.error('Error generating post variations:', error);
    throw new Error('Failed to generate post variations. Please try again.');
  }
}

/**
 * Analyzes post content and suggests improvements
 * @param {string} content - Post content to analyze
 * @param {string} platform - Target platform
 * @returns {Promise<Object>} Analysis and suggestions
 */
export async function analyzePost(content, platform = 'general') {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are a social media expert who provides constructive feedback on post content.' 
        },
        { 
          role: 'user', 
          content: `Analyze this ${platform} post and provide specific, actionable suggestions to improve engagement:\n\n"${content}"\n\nProvide: 1) Strengths, 2) Areas for improvement, 3) Specific suggestions` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'post_analysis',
          schema: {
            type: 'object',
            properties: {
              strengths: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Positive aspects of the post'
              },
              improvements: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Areas that could be better'
              },
              suggestions: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Specific actionable suggestions'
              },
              overallScore: { 
                type: 'number',
                minimum: 0,
                maximum: 10,
                description: 'Overall quality score out of 10'
              }
            },
            required: ['strengths', 'improvements', 'suggestions', 'overallScore'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'medium',
      verbosity: 'medium',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error analyzing post:', error);
    throw new Error('Failed to analyze post content. Please try again.');
  }
}